import { useEffect, useState } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { Login } from "./components/Login.js";
import { Shell } from "./components/Shell.js";
import { fetchAuthSession, isAuthConfigured } from "./lib/auth.js";
import { setUnauthorizedHandler } from "./lib/api.js";
import { hashForLogin, parseHash, pathForRoute, type Route } from "./lib/hashRoute.js";

export function App() {
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [route, setRoute] = useState<Route>(() => parseHash());

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSignedIn(false);
      const current = parseHash();
      const returnTo = current.kind === "login" ? "/" : pathForRoute(current);
      const next = hashForLogin(returnTo);
      if (location.hash !== next) location.hash = next;
    });
  }, []);

  useEffect(() => {
    void (async () => {
      if (!isAuthConfigured()) {
        setReady(true);
        return;
      }
      const session = await fetchAuthSession();
      setSignedIn(Boolean(session.tokens?.idToken));
      setReady(true);
    })();
  }, []);

  useEffect(() => {
    function onHash() {
      setRoute(parseHash());
    }
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (!signedIn && route.kind !== "login") {
      const next = hashForLogin(pathForRoute(route));
      if (location.hash !== next) location.hash = next;
    } else if (signedIn && route.kind === "login") {
      const dest = `#${route.returnTo}`;
      location.hash = dest.startsWith("#/login") ? "#/" : dest;
    } else if (signedIn && !location.hash) {
      location.hash = "#/";
    }
  }, [ready, signedIn, route]);

  if (!ready) {
    return <div className="flex min-h-full items-center justify-center text-sm text-gray-400">Loading...</div>;
  }

  if (!signedIn) {
    return <Login onSignedIn={() => setSignedIn(true)} />;
  }

  return (
    <ErrorBoundary resetKey={pathForRoute(route)}>
      <Shell route={route} onSignOut={() => setSignedIn(false)} />
    </ErrorBoundary>
  );
}
